import { useState, type FormEvent } from "react";

const PILLARS = [
  { icon: "ri-list-check-3", title: "一条线整理", desc: "联动、串门与跨圈事件按时间线归拢，来路与影响一目了然。" },
  { icon: "ri-shield-check-line", title: "标出来源", desc: "每则信息都区分实锤、路透与猜测，不拿传闻当结论。" },
  { icon: "ri-refresh-line", title: "跟进更新", desc: "事件有新进展就第一时间补上后续，避免吃瓜停在半路。" },
  { icon: "ri-group-line", title: "克制理性", desc: "尊重边界、不做人身攻击，倡导文明有度的吃瓜氛围。" },
];

export default function AboutSection() {
  const [status, setStatus] = useState<string>("");
  const [formError, setFormError] = useState<string>("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    // honeypot check
    const hp = String(fd.get("website_alt") || "").trim();
    if (hp) {
      setStatus("success");
      form.reset();
      return;
    }
    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });
    try {
      const res = await fetch(form.dataset.submit || form.action, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg =
        typeof meta.message === "string" && meta.message
          ? meta.message
          : typeof parsed?.message === "string"
            ? (parsed.message as string)
            : typeof meta.detail === "string"
              ? (meta.detail as string)
              : "";
      const isSpam =
        serverMsg.includes("spam") || responseText.toLowerCase().includes("form data is spam");
      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后再试。");
    }
  }

  return (
    <section id="about" className="scroll-mt-20 bg-background-100/60 py-10 md:py-14">
      <div className="container-x">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <p className="flex items-center gap-2 text-xs font-medium tracking-widest text-primary-600">
              <span className="h-px w-6 bg-primary-400" /> ABOUT
            </p>
            <h2 className="mt-2 font-display text-2xl text-foreground-900 md:text-3xl">
              关于 71吃瓜
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              71吃瓜是一档专注“跨圈”内容的吃瓜栏目。我们认为，娱乐圈真正有意思的瓜，往往发生在圈与圈的边界上——艺人与综艺的联动、作品间的跨界彩蛋、熟脸在别家舞台上的串门。我们把这些散落的信息收到同一条线上，边吃边理清关系，让后续更新不迷路。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              我们不追猎隐私，不做未经证实的定论，只做尽力可回溯、有来源口径的内容整理，愿陪你把每个跨圈瓜吃得明明白白。
            </p>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {PILLARS.map((p) => (
                <div
                  key={p.title}
                  className="flex items-start gap-3 rounded-2xl border border-background-200 bg-background-50 p-4"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-lg text-primary-600">
                    <i className={p.icon} />
                  </span>
                  <div>
                    <h3 className="font-medium text-foreground-900">{p.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* subscribe / contact form */}
          <div id="subscribe" className="rounded-3xl border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-display text-xl text-foreground-900 md:text-2xl">
              订阅更新 · 提供线索
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              留下邮箱，联动瓜、串门新料与跨圈后续更新到站即推；也欢迎把你看到的线索告诉我们。
            </p>

            {status === "success" ? (
              <div className="mt-6 flex flex-col items-center gap-3 rounded-2xl bg-primary-50 p-6 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-2xl text-background-50">
                  <i className="ri-check-line" />
                </span>
                <p className="font-medium text-foreground-900">已收到你的信息</p>
                <p className="text-sm text-foreground-600">
                  感谢关注 71吃瓜，我们会尽快处理并跟进。
                </p>
              </div>
            ) : (
              <>
                {formError && (
                  <div className="mt-4 flex items-start gap-2 rounded-xl bg-accent-100 p-3 text-sm text-accent-900">
                    <i className="ri-error-warning-line mt-0.5" />
                    <span>{formError}</span>
                  </div>
                )}
                <form
                  className="mt-5 flex flex-col gap-4"
                  data-readdy-form="71吃瓜 订阅与联系表单"
                  data-submit="https://readdy.ai/api/form/daek7nptto0ceqqngv0g"
                  action="https://readdy.ai/api/form/daek7nptto0ceqqngv0g"
                  method="post"
                  onSubmit={handleSubmit}
                >
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="name" className="text-sm text-foreground-700">
                      怎么称呼你
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      placeholder="你的昵称"
                      className="rounded-xl border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-sm text-foreground-700">
                      联系邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="you@example.com"
                      className="rounded-xl border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="message" className="text-sm text-foreground-700">
                      想订阅的板块 / 想提供的线索
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="例如：想跟进某剧与某综艺的联动，或分享你看到的串门线索……"
                      className="resize-none rounded-xl border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                    />
                  </div>
                  {/* honeypot */}
                  <div className="hp-field" aria-hidden="true">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                      aria-hidden="true"
                    />
                  </div>
                  <button
                    type="submit"
                    className="mt-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-accent-600"
                  >
                    <i className="ri-send-plane-line" /> 提交订阅
                  </button>
                  <p className="text-center text-xs text-foreground-400">
                    我们只用于推送更新，不会泄露你的信息。
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}